import { CommonProp } from '@acin/ui-core';
import React from 'react';
import { IInternalRegulationObligationRuleReviewStatus } from '../../../graphql/generated';
import { useMapControls } from '../../MapControlsProvider';
import { useInternalRegulationObligationRulesQuery } from '../graphql/queries/internalRegulationObligationRules.generated';
import { createObligationIdFilter } from '../hooks/filterConstants';
import { RuleItem } from './RuleItem';
import * as SC from './RulesList.styled';

export interface RulesListProps extends CommonProp {
  className?: string;
}

const PAGE_SIZE = 25;

export const RulesList: React.FC<RulesListProps> = ({ className }) => {
  const { activeObligation, activeObligationRule, onUpdateActiveObligationRule, confirmedRuleIds } = useMapControls();
  const { data, loading } = useInternalRegulationObligationRulesQuery({
    variables: {
      input: {
        filters: {
          groups: activeObligation ? [createObligationIdFilter(activeObligation.id)] : [],
        },
      },
      first: PAGE_SIZE,
    },
    skip: !activeObligation,
    onCompleted(data) {
      const activeRule = data?.internalRegulationObligationRules?.edges.find(
        ({ node }) => node.id === activeObligationRule?.id,
      );

      if (!activeRule || (!activeObligationRule && data?.internalRegulationObligationRules?.edges.length > 0)) {
        onUpdateActiveObligationRule(data?.internalRegulationObligationRules.edges[0].node);
      }
    },
  });

  const rules = data?.internalRegulationObligationRules?.edges.map((edge) => edge.node) ?? [];

  // Empty state - no obligation selected
  if (!activeObligationRule) {
    return (
      <SC.RulesList className={className}>
        <SC.EmptyState>Select an obligation to view its rules</SC.EmptyState>
      </SC.RulesList>
    );
  }

  // Loading state
  if (loading) {
    return (
      <SC.RulesList className={className}>
        <SC.EmptyState>Loading rules...</SC.EmptyState>
      </SC.RulesList>
    );
  }

  // No rules found
  if (rules.length === 0) {
    return (
      <SC.RulesList className={className}>
        <SC.EmptyState>No rules found for this obligation</SC.EmptyState>
      </SC.RulesList>
    );
  }

  // Render rules list
  return (
    <SC.RulesList className={className}>
      <SC.List>
        {rules.map((rule) => (
          <RuleItem
            key={rule.id}
            id={rule.id}
            name={rule.name}
            reviewStatus={
              confirmedRuleIds?.has(rule.id)
                ? IInternalRegulationObligationRuleReviewStatus.Reviewed
                : rule.reviewStatus
            }
            onSelect={() => onUpdateActiveObligationRule(rule)}
            onDelete={() => {
              // Handle deletion if needed
            }}
            isActive={activeObligationRule?.id === rule.id}
          />
        ))}
      </SC.List>
    </SC.RulesList>
  );
};
